/**
 * THE canonical refund / withdrawal policy and business disclosure.
 *
 * Spec: .claude/TODO/Refund-process-refactor-9-2.md — T0.3, T0.4, P5 (§10.4).
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Before this, we published SIX refund statements and they disagreed. Five said
 * unused whole months are refunded pro-rata; the subscription-cancelled email
 * said "이미 결제된 구독료는 환불되지 않습니다" — flatly the opposite, sent to a
 * customer at the exact moment they are deciding whether to ask for money back.
 *
 * A published refund term is a contract term, not copy. When two of them
 * conflict, the reading favourable to the 소비자 is the one that binds, so the
 * generous statement governs anyway and the restrictive one only costs us
 * credibility in a dispute. One source, rendered everywhere, is the fix.
 *
 * ⚠ EDITING RULES
 *   - Change the policy HERE, never at a call site. Every surface renders
 *     `REFUND_POLICY_CLAUSES`; a surface with its own wording is the bug this
 *     file was created to remove.
 *   - The backend keeps a byte-identical copy in
 *     `backend/app/services/refund_policy.py` for the email templates, which
 *     cannot import JavaScript. `backend/tests/test_refund_policy_parity.py`
 *     fails if the two drift. If you edit one, edit both.
 *
 * ⚠ ENGINEERING SUMMARY OF LAW, NOT LEGAL ADVICE. No legal review is recorded
 * anywhere in this repo. The statute references below are the reasoning for
 * each clause so a lawyer can check them quickly — they are not a warrant that
 * the reasoning is right. A Korean consumer-law lawyer should confirm before
 * these terms are relied on in a dispute.
 *
 * THE POSITION WE TOOK (§10.4): treat buyers as 소비자 by default. The carve-in
 * for a business buying "on the same terms as an ordinary consumer" is
 * ambiguous, and a small cosmetics brand buying a list-priced plan through a
 * self-service checkout sits squarely inside that ambiguity. Complying costs
 * little; being wrong costs refunds we believed were excluded plus a
 * merchant-review problem.
 */

/**
 * 통신판매업 신고번호 — 전자상거래법 제10조 requires this on the service.
 *
 * Supplied by the owner on 2026-10-02, closing the open item recorded here on
 * 2026-09-27. That closes THIS field only; it is not a claim that the 제10조
 * disclosure is complete (no legal review is on record — see the file header).
 * Every surface — the app footer, the landing footer (which /pricing renders
 * too) and /about — reads it from here.
 *
 * ⚠ Like bizRegNo, this is READ off the 신고증, never inferred or "tidied":
 * the spacing inside 제 … 호 is exactly as the owner supplied it. Change it HERE
 * and in the backend mirror (`backend/app/services/refund_policy.py`) together;
 * `backend/tests/test_refund_policy_parity.py` fails if the two differ.
 *
 * The surfaces still render it CONDITIONALLY, so a null would drop the line
 * everywhere — no label, no empty separator, no "null" — rather than print a
 * blank field. Do not go back to null: the field is required, and
 * src/__tests__/refundPolicySingleSource.test.js fails if it goes missing. If
 * the business ever becomes genuinely exempt, set it to the literal '면제' —
 * stating exemption explicitly is itself the compliant answer, and a reviewer
 * looking for the field must find something.
 */
export const ECOMMERCE_FILING_NO = '제 2026-서울마포-2298 호';

/**
 * Business disclosure — 전자상거래법 제10조. Rendered by the app footer, the
 * landing footer, the pricing page and About. THE ONLY COPY: every surface
 * reads these fields, and src/__tests__/businessDisclosureSingleSource.test.js
 * fails the build if any of them is typed as a literal anywhere else. Change a
 * value here and the whole product follows.
 *
 * ⚠ `bizRegNo` — READ OFF THE 사업자등록증, NEVER INFERRED OR "TIDIED".
 *
 * It was wrong once, and how that was found matters. Until 2026-10-14 this read
 * 704-13-02682, which FAILS the statutory check digit: the National Tax Service
 * algorithm (weights [1,3,7,1,3,7,1,3,5] over the first nine digits, plus
 * floor(d9*5/10); check = (10 - sum % 10) % 10) yields 1, not the 2 it carried.
 * It was kept on 2026-09-27 because the owner had confirmed it and a failing
 * check digit cannot tell a typo from a number nobody in the repo can see (ten
 * different single-digit edits of 02682 are all valid). The owner re-checked it
 * and corrected it to 704-13-02662 on 2026-10-14. That value passes the check
 * digit and is the one the April 2026 landing design carried
 * (src/landing-page-reference.html); the earlier verdict that 02662 was a
 * "transposed digit" was backwards.
 *
 * A passing check digit is necessary, not sufficient: it shows the number is
 * well formed, not that it is THEIRS. So the test guards the typo class (every
 * single-digit corruption of this value fails the check), and the value itself
 * still has to be read off the certificate whenever it changes. Change it HERE;
 * src/__tests__/businessDisclosureSingleSource.test.js fails if the check digit
 * breaks or a copy elsewhere drifts.
 */
export const BUSINESS_INFO = {
  nameKo: '웨어엣',
  nameEn: 'WhereAt',
  ceoKo: '심상미',
  ceoEn: 'Sangmi Shim',
  // Corrected by the owner 2026-10-14 (it read 704-13-02682, which failed the
  // check digit); see the note above.
  bizRegNo: '704-13-02662',
  addressKo: '서울특별시 마포구 양화로 136, 8층 807호',
  addressEn: '136, Yanghwa-ro, Mapo-gu, Seoul, 04038, Rep. of KOREA',
  tel: '02-6214-7114',
  email: 'hello@whereat.kr',
  ecommerceFilingNo: ECOMMERCE_FILING_NO,
};

/** Days from purchase in which withdrawal is unconditional. 전자상거래법 제17조제1항. */
export const WITHDRAWAL_WINDOW_DAYS = 7;

/** Business days we commit to for processing a released refund. */
export const REFUND_PROCESSING_DAYS = '3~5';

/**
 * The policy, clause by clause.
 *
 * Ordered as a customer reads it: what you bought → how to get out early →
 * what you get back → what you don't, and why → what it costs you (nothing) →
 * how to ask. `basis` is shown only where a customer benefits from knowing the
 * right is statutory rather than a favour; the rest carry it for reviewers.
 *
 * ⚠ WORDING — KG이니시스 merchant review, 2026-10-02
 * (.claude/TODO/Portone-KGinisys-Pricing-fix-10-2.md §3). KG classifies credits
 * as 환금성 (cash-equivalent) and puts that merchant on a separate, stricter
 * review track. Describe a SERVICE with a fixed quantity: 분석 리포트 N건,
 * 제공 / "provided" each month. Never credits, 지급 / "granted", 충전, 잔액,
 * 이용권, or 선불 next to the unit. The refund unit is the month's FEE: never call
 * a report refundable or non-refundable. Clause ids are internal identifiers
 * and keep their names (`prepaid` included) whatever the text says.
 */
export const REFUND_POLICY_CLAUSES = [
  {
    id: 'prepaid',
    ko: '모든 요금제는 12개월 이용 상품입니다. 결제일에 12개월 이용료 전액이 한 번에 결제되며, 이용 기간 동안 매월 요금제에 포함된 분석 리포트가 제공됩니다.',
    en: 'All plans are 12-month service plans. The full 12-month fee is charged once on the purchase date, and the analysis reports included in the plan are provided each month of the term.',
  },
  {
    id: 'nocash',
    // ⚠ D-4: pending legal sign-off before deploy (D-4). Unlike the rewordings
    // around it, this clause ADDS a contract term, so a lawyer approves it
    // before it ships. The text is the plan's D-4 wording verbatim, and it
    // states what is already true in code: nothing is sold standalone, nothing
    // transfers between accounts or organizations, nothing cashes out, and a
    // refund is whole months of fee (refund_calculator.py). It sits second, in
    // the same position in refund_policy.py, because it completes "what you
    // bought".
    ko: '분석 리포트 제공 건수는 요금제에 포함된 서비스 제공량으로, 별도로 판매되지 않으며 현금으로 교환하거나 다른 계정·조직에 양도할 수 없습니다. 환불은 이용하지 않은 개월 단위로 계산되어 결제하신 수단으로 처리됩니다.',
    en: 'The monthly report allowance is part of your plan. It is not sold separately and cannot be exchanged for cash or transferred to another account or organization. Refunds are calculated in unused whole months and returned to your original payment method.',
  },
  {
    id: 'withdrawal',
    ko: `결제일로부터 ${WITHDRAWAL_WINDOW_DAYS}일 이내에는 청약철회가 가능합니다. 서비스 이용을 시작하신 경우에는 이미 제공된 부분을 제외한 나머지 기간에 대해 철회하실 수 있습니다.`,
    en: `You may withdraw within ${WITHDRAWAL_WINDOW_DAYS} days of purchase. If you have already begun using the service, you may withdraw the portion not yet supplied.`,
    basisKo: '전자상거래법 제17조제1항, 제17조제2항제5호 단서',
    basisEn: 'Act on Consumer Protection in Electronic Commerce, Art. 17(1) and the proviso to Art. 17(2)5',
  },
  {
    id: 'midterm',
    ko: '이용 기간 중에는 언제든지 중도 해지를 요청하실 수 있습니다. 사유를 밝히지 않으셔도 됩니다.',
    en: 'You may request mid-term termination at any point during the term. No reason is required.',
    basisKo: '방문판매법 제31조 (계속거래)',
    basisEn: 'Door-to-Door Sales Act, Art. 31 (continuing transactions)',
  },
  {
    id: 'prorata',
    ko: '환불액은 아직 이용하지 않은 개월 수에 월 환산 요금을 곱해 계산합니다. 2회 이상 부분 환불하시는 경우, 다음 환불액은 이미 단축된 종료일을 기준으로 다시 계산됩니다.',
    en: 'The refund is the number of months not yet used multiplied by the monthly rate. If you refund part of the term more than once, the next refund is recalculated from the already-shortened end date.',
  },
  {
    id: 'currentmonth',
    // Q5 (2026-09-11) — the current month is CONDITIONAL, not automatic.
    // It used to say the month in progress was not refunded whatever had been
    // used, i.e. a whole month forfeited even on day 1 with nothing supplied.
    // 방문판매법 제32조제3항, the clause's own cited basis, permits deducting only the
    // portion ACTUALLY supplied — so the old wording was the weakest point in this
    // policy. Nothing used, nothing deducted.
    //
    // 2026-10-03 (KG이니시스 review) — what is refunded or kept is the month's
    // FEE. The previous tail also declared the month's units non-refundable,
    // which presented them as value that could be refunded; it is gone, and the
    // months refunded are unchanged. Keep "이미 제공" / "already been supplied"
    // (the reason, §10.4) and "사용하지 않으" (the condition): both are pinned.
    ko: `이번 달에 제공된 분석 리포트를 한 건도 사용하지 않으셨다면 이번 달 이용료도 환불해 드립니다. 한 건이라도 사용하셨다면 이번 달 서비스는 이미 제공이 완료된 것으로 보아 이번 달 이용료는 환불되지 않습니다.`,
    en: `If none of the analysis reports provided for the current month have been used, the current month is refunded too. If any have been used, that month has already been supplied and its fee is not refunded.`,
    basisKo: '방문판매법 제32조제3항 (이미 제공된 부분의 공제)',
    basisEn: 'Door-to-Door Sales Act, Art. 32(3) — deduction of the portion already supplied',
  },
  {
    id: 'nopenalty',
    ko: '중도 해지에 따른 위약금은 청구하지 않습니다.',
    en: 'No cancellation penalty is charged.',
  },
  {
    id: 'shortened',
    ko: '환불이 완료되면 이용 기간이 그만큼 단축되며, 단축된 기간에는 분석 리포트가 제공되지 않습니다. 전체 기간을 환불하시면 요금제가 종료되고 무료 플랜으로 전환됩니다.',
    en: 'Once a refund is released your term is shortened accordingly, and no analysis reports are provided for the refunded months. Refunding the whole remaining term ends the plan and returns the account to the free tier.',
  },
  {
    id: 'method',
    ko: `환불은 최초 결제하신 수단으로 처리되며, 승인 후 영업일 기준 ${REFUND_PROCESSING_DAYS}일 이내에 완료됩니다. 카드 결제의 경우 카드사 처리 일정에 따라 실제 반영은 더 걸릴 수 있습니다.`,
    en: `Refunds are returned to the original payment method within ${REFUND_PROCESSING_DAYS} business days of approval. For card payments, your card issuer may take longer to show the refund on your statement.`,
  },
  {
    id: 'contact',
    ko: '환불 요청은 설정 > 구독 화면에서 직접 하실 수 있으며, hello@whereat.kr 로 문의하셔도 됩니다. 접수된 요청은 영업일 기준 1일 이내에 검토합니다.',
    en: 'Request a refund from Settings → Subscription, or email hello@whereat.kr. Requests are reviewed within 1 business day.',
  },
];

/** One-line summary, for places with no room for the full clause list. */
export const REFUND_POLICY_SUMMARY = {
  ko: `이용하지 않은 개월은 월 단위로 환불됩니다. 이번 달은 제공된 분석 리포트를 한 건도 사용하지 않으신 경우에만 환불되며, 위약금은 없습니다. 결제일로부터 ${WITHDRAWAL_WINDOW_DAYS}일 이내에는 청약철회가 가능합니다.`,
  en: `Unused whole months are refunded. The current month is refunded only if none of its analysis reports have been used, and there is no cancellation penalty. You may withdraw within ${WITHDRAWAL_WINDOW_DAYS} days of purchase.`,
};

/**
 * The 청약철회 notice shown AT CHECKOUT, before payment.
 *
 * ⚠ THIS DISPLAY IS LOAD-BEARING, not informational. 전자상거래법 제17조제6항 makes
 * displaying the restriction a PRECONDITION for the restriction binding at all.
 * Undisplayed, the customer keeps the full unrestricted withdrawal right and
 * our "the month in progress is refunded only if none of its reports were
 * used" restriction simply does not apply. Removing this block therefore does
 * not simplify the checkout — it silently widens our refund liability.
 *
 * D-3 (option B, 2026-10-03): the last sentence states the published Q5 rule
 * (`currentmonth` above). It used to say the month in progress was never
 * refunded, which was stricter than the policy and the calculator, at the
 * point of sale; the consumer-favourable reading binds anyway (header above).
 */
export const CHECKOUT_WITHDRAWAL_NOTICE = {
  ko:
    `결제일로부터 ${WITHDRAWAL_WINDOW_DAYS}일 이내에 청약철회하실 수 있습니다. ` +
    '다만 서비스 이용을 시작하신 이후에는 이미 제공된 부분을 제외한 나머지 기간에 대해서만 철회가 가능합니다. ' +
    '이용 기간 중에는 언제든지 중도 해지를 요청하실 수 있으며, 이용하지 않은 개월은 월 단위로 환불됩니다. ' +
    '진행 중인 달의 이용료는 그 달에 제공된 분석 리포트를 한 건도 사용하지 않으신 경우에만 환불됩니다. 위약금은 없습니다.',
  en:
    `You may withdraw within ${WITHDRAWAL_WINDOW_DAYS} days of purchase. ` +
    'Once you begin using the service, withdrawal covers the portion not yet supplied. ' +
    'You may request mid-term termination at any time, and unused whole months are refunded. ' +
    'The fee for the month in progress is refunded only if none of the analysis reports provided for that month have been used. There is no cancellation penalty.',
};

/** Pick the localized side of any clause-shaped object. */
export const clauseText = (clause, isKo) => (isKo ? clause.ko : clause.en);
export const clauseBasis = (clause, isKo) => (isKo ? clause.basisKo : clause.basisEn);
